/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Modal from 'react-modal';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faCircleStop, faPencil, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { useVoiceInput } from 'react-voice-to-text-converter';
import { toast } from 'react-toastify';

const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: false,
  typographer: true,
});

const analysisInstructions = "Ask me to analyze your financial data, compare against peers, draft advisory emails, or answer specific questions about your client."
const practiceInstructions = "Ask me about your clients' financial health and industry breakdowns. Tip: use the filters to narrow down by NAICS code, industry, or year for more targeted answers."

const CopyButton = ({ text, getRenderedHtml }) => {
  const [copied, setCopied] = useState(false);

  const inlineStyles = {
    div:        "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;line-height:1.6;color:#111;background:none;",
    p:          "margin:0 0 12px 0;background:none;",
    h1:         "font-size:22px;font-weight:700;margin:0 0 12px 0;background:none;",
    h2:         "font-size:18px;font-weight:700;margin:0 0 10px 0;background:none;",
    h3:         "font-size:15px;font-weight:700;margin:0 0 8px 0;background:none;",
    strong:     "font-weight:700;background:none;",
    em:         "font-style:italic;background:none;",
    ul:         "margin:0 0 12px 0;padding-left:20px;background:none;",
    ol:         "margin:0 0 12px 0;padding-left:20px;background:none;",
    li:         "margin-bottom:4px;background:none;",
    hr:         "border:none;border-top:1px solid #e5e7eb;margin:16px 0;background:none;",
    code:       "font-family:monospace;font-size:13px;padding:2px 5px;border-radius:4px;background:none;",
    pre:        "font-family:monospace;font-size:13px;padding:12px;border-radius:6px;margin:0 0 12px 0;overflow-x:auto;background:none;",
    blockquote: "border-left:3px solid #d1d5db;margin:0 0 12px 0;padding:4px 12px;color:#6b7280;background:none;",
    a:          "color:#2563eb;text-decoration:underline;background:none;",
  };

  const applyInlineStyles = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // No background on every single element
    doc.querySelectorAll('*').forEach(el => {
      el.style.background = 'none';
      el.style.backgroundColor = 'transparent';
    });

    // Then apply specific styles per tag
    Object.entries(inlineStyles).forEach(([tag, style]) => {
      doc.querySelectorAll(tag).forEach(el => {
        el.setAttribute('style', (el.getAttribute('style') || '') + style);
      });
    });

    // Wrap td/th content in <p> and move styles into <p> because tiptap text editor puts table cell values in <p> tags
    doc.querySelectorAll('td, th').forEach(cell => {
      const p = doc.createElement('p');

      // move existing inline styles from cell to p
      p.setAttribute('style', cell.getAttribute('style') || '');
      // move content into <p>
      p.innerHTML = cell.innerHTML;

      // replace cell content with <p>
      cell.innerHTML = '';
      cell.appendChild(p);

      // clear cell styles if only want them on <p>
      cell.removeAttribute('style');
    });

    return doc.body.innerHTML;
  };

 const copyRichText = (styledHtml, plainText) => {
    // Creates a temporary contenteditable div with the rich HTML,
    // selects it, and uses execCommand('copy') — works on HTTP too.
    const el = document.createElement('div');
    el.setAttribute('contenteditable', 'true');
    el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
    el.innerHTML = styledHtml;
    document.body.appendChild(el);

    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('execCommand copy failed:', err);
    }

    sel.removeAllRanges();
    document.body.removeChild(el);
  };

  const handleCopy = async () => {
    const rawHtml = getRenderedHtml();
    const inlined = applyInlineStyles(rawHtml);
    const styledHtml = `<div style='${inlineStyles.div}'>${inlined}</div>`;

    if (navigator?.clipboard?.write) {
      // HTTPS: use modern clipboard API
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([styledHtml], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
      } catch (err) {
        console.error('clipboard.write failed:', err);
        copyRichText(styledHtml, text);
      }
    } else {
      // HTTP / no clipboard API: use execCommand with rich HTML
      copyRichText(styledHtml, text);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      className={`copilot-copy-btn ${copied ? 'copilot-copy-btn--copied' : ''}`}
      onClick={handleCopy}
      title='Copy'
    >
      {copied ? (
        // Checkmark icon
        <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
          <polyline points='20 6 9 17 4 12' />
        </svg>
      ) : (
        // Copy icon
        <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <rect x='9' y='9' width='13' height='13' rx='2' />
          <path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
        </svg>
      )}
    </button>
  );
}

const CopilotSlideOut = ({
  isOpen,
  onClose,
  activeTab,
  companyName,
  userType,
  setupProd,
  page,
  dataGiven,
  filters: reportFilters,
}) => {
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [slideOutWidth, setSlideOutWidth] = useState(504);
  const [adminView, setAdminView] = useState(userType === 'Admin');
  const [prompts, setPrompts] = useState({ default: [], custom: [], other: [] });
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState({ id: null, name: '', role: '', prompt: '', prompt_type: '', permissions: 'all', auto_generate: false, tab_order: 0, sort_data_by: '' });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const subscriptionRef = useRef(null);
  const subscribedChatIdRef = useRef(null);
  const isResized = useRef(false);
  const [minWidth, maxWidth, defaultWidth] = [209, 1200, 504];

  const handleVoiceTranscript = useCallback((text, isFinal) => {
    // Update DOM for real-time feel
    if (inputRef.current) {
      inputRef.current.textContent = text;
    }
    setInputValue(text);

    // Auto-save on final
    if (isFinal) {
      console.log('Saved:', text);
    }
  }, []);

  const {
    isListening,
    isSupported,
    toggleListening,
    setBaseText
  } = useVoiceInput({
    onTranscript: handleVoiceTranscript,
    language: 'en-US'
  });

  const handleToggleVoice = () => {
    if (!isListening) {
      setBaseText(inputRef.current?.textContent || '');
    }
    toggleListening();
  };

  useEffect(() => {
    if (streaming) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingContent, streaming]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResized.current) {
        return;
      }

      document.body.style.userSelect = 'none';
      setSlideOutWidth((prev) => {
        const newWidth = window.innerWidth - e.clientX;
        return newWidth >= minWidth && newWidth <= maxWidth ? newWidth : prev;
      });
    };

    const onMouseUp = () => {
      if (!isResized.current) {
        return;
      }

      document.body.style.userSelect = '';
      isResized.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [minWidth, maxWidth]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
        subscribedChatIdRef.current = null;
      }
    };
  }, []);

  const fetchPrompts = async () => {
    try {
      const data = await apiFetch(`/api/v1/copilot_prompts?page=${page}`);
      setPrompts(data);
    } catch (err) {
      console.error('Failed to fetch prompts:', err);
    }
  };

  const savePrompt = async () => {
    if (!currentPrompt.name.trim() || !currentPrompt.prompt.trim()) {
      alert('Please fill in both name and prompt text.');
      return;
    }

    try {
      const method = currentPrompt.id ? 'PATCH' : 'POST';
      const url = currentPrompt.id ? `/api/v1/copilot_prompts/${currentPrompt.id}` : '/api/v1/copilot_prompts';
      const payload = {
        copilot_prompt: {
          name: currentPrompt.name,
          role: currentPrompt.role,
          prompt: currentPrompt.prompt,
          page: page,
          prompt_type: (adminView && userType === 'Admin') || currentPrompt.prompt_type === 'admin_default' ? 'admin_default' : 'custom',
          permissions: currentPrompt.permissions,
          auto_generate: currentPrompt.auto_generate,
          tab_order: currentPrompt.tab_order,
          sort_data_by: currentPrompt.sort_data_by
        }
      };

      await apiFetch(url, {
        method: method,
        body: JSON.stringify(payload),
      });

      await fetchPrompts();
      if (!currentPrompt.id) { 
        setIsPromptModalOpen(false);
        let sortData = page === "practice" ? "peerview_rank" : ""
        setCurrentPrompt({ id: null, name: '', role: '', prompt: '', prompt_type: '', permissions: 'all', auto_generate: false, tab_order: 0, sort_data_by: sortData });
      }
      toast.success('Prompt saved!', { autoClose: 3000 })
    } catch (err) {
      alert('Failed to save prompt.');
      console.error('Save prompt error:', err);
    }
  };

  const deletePrompt = async (id) => {
    if (!confirm('Are you sure you want to delete this prompt?')) return;

    try {
      await apiFetch(`/api/v1/copilot_prompts/${id}`, {
        method: 'DELETE',
      });

      await fetchPrompts();
    } catch (err) {
      alert('Failed to delete prompt.');
      console.error('Delete prompt error:', err);
    }
  };

  const openEditModal = (prompt) => {
    let sortData = page === "practice" && prompt.sort_data_by ? prompt.sort_data_by : page === "practice" ? "peerview_rank" : '';
    setCurrentPrompt({ id: prompt.id, name: prompt.name, role: prompt.role, prompt: prompt.prompt, prompt_type: prompt.prompt_type, permissions: prompt.permissions, auto_generate: prompt.auto_generate, tab_order: prompt.tab_order, sort_data_by: prompt.sort_data_by });
    setIsPromptModalOpen(true);
  };

  const openNewPromptModal = () => {
    let sortData = page === "practice" ? "peerview_rank" : ""
    setCurrentPrompt({ id: null, name: '', role: '', prompt: '', prompt_type: '', permissions: 'all', auto_generate: false, tab_order: 0, sort_data_by: sortData });
    setIsPromptModalOpen(true);
  };

  useEffect(() => {
    if (isOpen) {
      fetchPrompts();
    }
  }, [isOpen]);

  const apiFetch = (path, options = {}) => {
    const headers = {
      'X-CSRF-Token': window.token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    };
    return fetch(path, { ...options, headers }).then((r) => {
      if (!r.ok) throw new Error(`Server error: ${r.status}`);
      return r.json();
    });
  };

  const subscribeToCopilot = (newChatId) => {
    if (subscribedChatIdRef.current === newChatId) return;

    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    subscriptionRef.current = App.cable.subscriptions.create(
      {
        channel: 'CopilotChannel',
        chat_id: newChatId,
      },
      {
        received: (data) => {
          if (data.token) {
            setStreamingContent((prev) => prev + data.token);
          }
          if (data.finish) {
            setStreaming(false);
            setStreamingContent('');
            if (data.message) {
              setMessages((prev) => [
                ...prev,
                {
                  id: data.message.id,
                  role: 'assistant',
                  content: data.message.display_content,
                },
              ]);
            }
          }
          if (data.error) {
            setStreaming(false);
            setStreamingContent('');
            setError(data.error);
          }
        },
      }
    );
    subscribedChatIdRef.current = newChatId;
  };

  const sendMessage = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || streaming) return;

    if (!reportFilters) {
      setError('Financial data is still loading. Please wait a moment and try again.');
      return;
    }

    setError(null);
    setStreaming(true);
    setStreamingContent('');

    const userMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    try {
      const payload = { page: page, message: trimmed, filters: reportFilters };

      if (chatId) {
        payload.chat_id = chatId;
      }

      if (selectedPrompt) {
        payload.prompt_id = selectedPrompt;
      }

      if (dataGiven) {
        payload.data_given = dataGiven;
      }
      // May need to send prompt ID instead of text if using default/custom prompts
      const res = await apiFetch('/api/v1/copilot_v2', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const newChatId = res.chat_id;

      if (!chatId) {
        setChatId(newChatId);
      }

      subscribeToCopilot(newChatId);
    } catch (err) {
      setStreaming(false);
      setError('Failed to send message. Please try again.');
      console.error('Copilot error:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
      subscribedChatIdRef.current = null;
    }
    setChatId(null);
    setMessages([]);
    setStreamingContent('');
    setError(null);
    setHistoryOpen(false);
  };

  const loadHistory = async () => {
    try {
      const data = await apiFetch(`/api/v1/copilot_v2/history/${page}`);
      setChatHistory(data);
      setHistoryOpen(true);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const loadChat = async (id) => {
    try {
      const data = await apiFetch(`/api/v1/copilot_v2/${id}`);
      setChatId(data.id);
      setMessages(
        data.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.display_content,
        }))
      );
      setHistoryOpen(false);
      subscribeToCopilot(data.id);
    } catch (err) {
      console.error('Failed to load chat:', err);
    }
  };

  // const simpleMarkdown = (text) => {
  //   if (!text) return '';
  //   let html = text
  //     .replace(/&/g, '&amp;')
  //     .replace(/</g, '&lt;')
  //     .replace(/>/g, '&gt;');

  //   html = html.replace(/^### (.+)$/gm, '<h4>$1</h4>');
  //   html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');
  //   html = html.replace(/^# (.+)$/gm, '<h2>$1</h2>');

  //   html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  //   html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  //   html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  //   html = html.replace(/\n\n/g, '</p><p>');
  //   html = html.replace(/\n/g, '<br/>');

  //   html = html.replace(/(<br\/>)- /g, '<br/>&#8226; ');
  //   html = html.replace(/^- /gm, '&#8226; ');

  //   html = html.replace(/(<br\/>)(\d+)\. /g, '<br/>$2. ');

  //   return DOMPurify.sanitize('<p>' + html + '</p>');
  // };

  const renderMarkdown = (text) => {
    if (!text) return '';
    text = text.replace(/(?:\n|^)?:::writing block(?:\n|$)?/g, '');
    return DOMPurify.sanitize(md.render(text), {
      ALLOWED_ATTR: ['style', 'href', 'target', 'rel', 'class'],
    });
  };

  const renderedStreaming = useMemo(() => renderMarkdown(streamingContent), [streamingContent]);

  const renderMessage = (msg, index) => {
    const isUser = msg.role === 'user';
    return (
      <div key={index} className={`copilot-message ${isUser ? 'copilot-message-user' : 'copilot-message-assistant'}`}>
        <div className={`copilot-message-bubble ${isUser ? 'copilot-bubble-user' : 'copilot-bubble-assistant'}`}>
          {isUser ? (
            <p>{msg.content}</p>
          ) : (
            <>
              <div
                className='copilot-markdown'
                dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
              />
              <div className='copilot-message-actions'>
                <CopyButton
                  text={msg.content}
                  getRenderedHtml={() => renderMarkdown(msg.content)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const handleCustomPrompts = (prompt = null, customPrompt = false) => {
    if (customPrompt) {
      setInputValue(prompt.name);
      setSelectedPrompt(prompt.id);
    } else {
      setInputValue(prompt);
      setSelectedPrompt(null);
    }
  }

  const handleInputValue = (value) => {
    if (value.length <= 0) {
      setSelectedPrompt(null)
    }
    setInputValue(value)
  }

  const contextPill = () => {
    let tabName = '';
    if (page === 'practice') {
      tabName = 'Practice Report'
    } else if (page === 'firm') {
      tabName = 'Firm Report'
    } else {
      tabName = activeTab?.name || 'Report'
    }
    const client = companyName || 'Client';
    return `${client} — ${tabName}`;
  };

  // <button className='copilot-suggestion' onClick={() => handleCustomPrompts('Run a full financial analysis')}>
  //   Run a full financial analysis
  // </button>
  // <button className='copilot-suggestion' onClick={() => handleCustomPrompts('What are the top areas for improvement?')}>
  //   Top areas for improvement
  // </button>
  // <button className='copilot-suggestion' onClick={() => handleCustomPrompts('Draft an advisory email to the client')}>
  //   Draft an advisory email
  // </button>

  const editablePrompts = adminView && userType === 'Admin' ? [...prompts.default, ...prompts.custom] : prompts.custom;
  const pageIntstructions = page === 'practice' || page === 'firm' ? practiceInstructions : analysisInstructions;

  return (
    <div className={`copilot-slideout-container ${isOpen ? 'copilot-slideout-open' : ''}`} style={{ 'width': `${slideOutWidth}px`}}>
      <div 
        className='copilot-slideout-handle'
        onMouseDown={() => {
          isResized.current = true;
        }}
      />
      <div className={`copilot-slideout`}>
        <div className='copilot-header'>
          <div className='copilot-header-left'>
            <h3 className='copilot-title'>
              <i className='fa fa-comments' /> Ask Peerview
            </h3>
            {slideOutWidth > 404 ? <span className='copilot-context-pill'>{contextPill()}</span> : null}
          </div>
          <div className='copilot-header-actions'>
            <button className='copilot-header-btn' onClick={startNewChat} title='New Chat'>
              <i className='fa fa-plus' />
            </button>
            <button className='copilot-header-btn' onClick={loadHistory} title='History'>
              <i className='fa fa-history' />
            </button>
            <button className='copilot-header-btn' onClick={onClose} title='Close'>
              <i className='fa fa-times' />
            </button>
          </div>
        </div>

        <div className='copilot-body'>
          {historyOpen ? (
            <div className='copilot-history'>
              <h4>Conversation History</h4>
              {chatHistory.length === 0 ? (
                <p className='copilot-empty'>No previous conversations.</p>
              ) : (
                chatHistory.map((chat) => (
                  <div key={chat.id} className='copilot-history-item' onClick={() => loadChat(chat.id)}>
                    <div className='copilot-history-title'>{chat.title}</div>
                    <div className='copilot-history-preview'>{chat.preview}</div>
                    <div className='copilot-history-date'>
                      {new Date(chat.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))
              )}
              <button className='copilot-back-btn' onClick={() => setHistoryOpen(false)}>
                <i className='fa fa-arrow-left' /> Back to Chat
              </button>
            </div>
          ) : (
            <div className='copilot-messages'>
              {messages.length === 0 && !streaming && (
                <div className='copilot-welcome'>
                  <div className='copilot-welcome-icon'>
                    <i className='fa fa-comments' />
                  </div>
                  <h4 className='copilot-welcome-title'>Welcome to Ask Peerview</h4>
                  <p>{pageIntstructions}</p>
                  <div className='copilot-suggestions'>
                    <div className='copilot-default-prompts'>
                      {prompts.default.map(prompt => (
                        <button key={prompt.id} className='copilot-suggestion' onClick={() => handleCustomPrompts(prompt, true)}>
                          {prompt.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className='copilot-custom-prompts'>
                    <div className='copilot-custom-prompts-header'>
                      <h5 className='copilot-custom-prompts-title'>Your Prompts</h5>
                      <button
                        className='copilot-suggestion copilot-add-prompt-btn'
                        onClick={openNewPromptModal}
                      >
                        <i className='fa fa-plus' /> Add Prompt
                      </button>
                    </div>
                    <div className='copilot-custom-prompts-section'>
                      {prompts.custom.map(prompt => (
                        <button key={prompt.id} className='copilot-suggestion' onClick={() => handleCustomPrompts(prompt, true)}>
                          {prompt.name}
                        </button>
                      ))}
                      {prompts.other.map(prompt => (
                        <button key={prompt.id} className='copilot-suggestion' onClick={() => handleCustomPrompts(prompt, true)}>
                          {prompt.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {messages.map((msg, i) => renderMessage(msg, i))}
              {streaming && streamingContent && (
                <div className='copilot-message copilot-message-assistant'>
                  <div className='copilot-message-bubble copilot-bubble-assistant'>
                    <div
                      className='copilot-markdown'
                      dangerouslySetInnerHTML={{ __html: renderedStreaming }}
                    />
                    <span className='copilot-cursor' />
                  </div>
                </div>
              )}
              {streaming && !streamingContent && (
                <div className='copilot-message copilot-message-assistant'>
                  <div className='copilot-message-bubble copilot-bubble-assistant'>
                    <div className='copilot-thinking'>
                      <span className='copilot-dot' />
                      <span className='copilot-dot' />
                      <span className='copilot-dot' />
                    </div>
                  </div>
                </div>
              )}
              {error && (
                <div className='copilot-error'>
                  <i className='fa fa-exclamation-triangle' /> {error}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className='copilot-input-area'>
          <textarea
            ref={inputRef}
            className='copilot-input'
            value={inputValue}
            onChange={(e) => handleInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your client's financials..."
            rows={2}
            disabled={streaming}
          />
          {
            isListening
            ? (<button className='copilot-mic-stop-btn' onClick={() => handleToggleVoice('stop')} title='Stop Listening'>
                  <FontAwesomeIcon icon={faCircleStop} />
                </button>)
            : (<button className='copilot-mic-btn' onClick={() => handleToggleVoice('listen')} disabled={streaming || !isSupported} title={!isSupported ? "Speech recognition not supported in this browser" : ""}>
                <FontAwesomeIcon icon={faMicrophone} />
              </button>)
          }
          <button
            className='copilot-send-btn'
            onClick={sendMessage}
            disabled={streaming || !inputValue.trim()}
          >
            <i className='fa fa-paper-plane' />
          </button>
        </div>
      </div>

      <Modal
        isOpen={isPromptModalOpen}
        onRequestClose={() => setIsPromptModalOpen(false)}
        shouldCloseOnOverlayClick={true}
        overlayClassName='copilot-prompt-modal-overlay'
        className='copilot-prompt-modal-content'
      >
        <div style={{ display: 'flex' }}>
          <div style={{ width: '80%' }}>
            {
              userType === 'Admin' && (
                <div className='copilot-admin-toggle'>
                  <h3>{adminView ? 'Admin View' : 'User View'}</h3>
                  <button
                    className={`copilot-admin-btn ${adminView ? 'active' : ''}`}
                    onClick={() => setAdminView(!adminView)}
                  >
                    {adminView ? 'Switch to User View' : 'Switch to Admin View'}
                  </button>
                </div>
              )
            }

            <div className='copilot-prompt-modal-header'>
              <div>
                <h3 className='copilot-prompt-modal-title'>
                  {currentPrompt.id ? 'Edit Prompt' : 'Create New Prompt'}
                  {currentPrompt.id ? <FontAwesomeIcon icon={faPencil} style={{ marginLeft: '10px' }} /> : null}
                </h3>
              </div>
            </div>

            <div className='copilot-prompt-form-group'>
              <label className='copilot-prompt-label'>Prompt Name</label>
              <input
                type='text'
                value={currentPrompt.name}
                onChange={(e) => setCurrentPrompt({ ...currentPrompt, name: e.target.value })}
                placeholder='Quick Analysis'
                className='copilot-prompt-input'
              />
            </div>

            <div className='copilot-prompt-form-group'>
              <label className='copilot-prompt-label'>Prompt Role</label>
              <input
                type='text'
                value={currentPrompt.role}
                onChange={(e) => setCurrentPrompt({ ...currentPrompt, role: e.target.value })}
                placeholder='You are an Analyst'
                className='copilot-prompt-input'
              />
            </div>

            <div className='copilot-prompt-form-group copilot-prompt-textarea-group'>
              <label className='copilot-prompt-label'>Prompt Text</label>
              <textarea
                value={currentPrompt.prompt}
                onChange={(e) => setCurrentPrompt({ ...currentPrompt, prompt: e.target.value })}
                placeholder="Ex. Run an analysis and tell me three ways this company is outperforming their peers, three ways they're underperforming their peers, and the three best projects we could take on to help them improve performance and financial health."
                className='copilot-prompt-textarea'
              />
            </div>

            <div className='copilot-prompt-form-group copilot-prompt-permissions'>
              <div className='copilot-prompt-label'>Permissions</div>
              <div>
                <label style={{ marginRight: '10px' }}>
                  <input
                    type='checkbox'
                    checked={currentPrompt.permissions === 'private'}
                    onChange={() => setCurrentPrompt({ ...currentPrompt, permissions: 'private' })}
                  /> Private (only visible to you)
                </label>
                <label>
                  <input
                    type='checkbox'
                    checked={currentPrompt.permissions === 'all'}
                    onChange={() => setCurrentPrompt({ ...currentPrompt, permissions: 'all' })}
                  /> {adminView ? 'All Users' : 'All Users (visible to everyone in your firm)'}
                </label>
              </div>
            </div>

            {adminView && ['practice', 'firm'].includes(page) && (
              <div className='copilot-prompt-form-group copilot-prompt-permissions'>
                <div className='copilot-prompt-label'>Generate as a Report</div>
                <div>
                  <label style={{ marginRight: '10px' }}>
                    <input
                      type='checkbox'
                      checked={currentPrompt.auto_generate === true}
                      onChange={() => setCurrentPrompt({ ...currentPrompt, auto_generate: true })}
                    /> Yes
                  </label>
                  <label>
                    <input
                      type='checkbox'
                      checked={currentPrompt.auto_generate === false}
                      onChange={() => setCurrentPrompt({ ...currentPrompt, auto_generate: false })}
                    /> No
                  </label>
                </div>
              </div>
            )}

            {adminView && ['practice', 'firm'].includes(page) && (
              <div className='copilot-prompt-form-group copilot-prompt-permissions'>
                <div>
                  <label style={{ marginRight: '10px' }}>Tab Order</label>
                  <input
                    type='number'
                    value={currentPrompt.tab_order}
                    onChange={(e) => setCurrentPrompt({ ...currentPrompt, tab_order: e.target.value })}
                  />
                </div>
              </div>
            )}

            {adminView && page === 'practice' && (
              <div className='copilot-prompt-form-group copilot-prompt-permissions'>
                <div className='copilot-prompt-label'>Sort Practice Data By</div>
                <div>
                  <label style={{ marginRight: '10px' }}>
                    <input
                      type='checkbox'
                      checked={currentPrompt.sort_data_by === "peerview_rank"}
                      onChange={() => setCurrentPrompt({ ...currentPrompt, sort_data_by: "peerview_rank" })}
                    /> Peerview Rank
                  </label>
                  <label>
                    <input
                      type='checkbox'
                      checked={currentPrompt.sort_data_by === "total_revenue"}
                      onChange={() => setCurrentPrompt({ ...currentPrompt, sort_data_by: "total_revenue" })}
                    /> Revenue Size (Largest to Smallest)
                  </label>
                </div>
              </div>
            )}

            <div className='copilot-prompt-actions'>
              <button onClick={savePrompt} className='copilot-prompt-save-btn'>
                SAVE PROMPT
              </button>
              <button onClick={() => setIsPromptModalOpen(false)} className='copilot-prompt-cancel-btn'>
                CANCEL
              </button>
            </div>

            {currentPrompt.id && (
              <button
                onClick={() => {
                  deletePrompt(currentPrompt.id);
                  setIsPromptModalOpen(false);
                }}
                className='copilot-prompt-delete-btn'
              >
                DELETE PROMPT
              </button>
            )}
          </div>
          <div className='copilot-prompts-saved'>
            <button
              onClick={() => setIsPromptModalOpen(false)}
              className='copilot-prompt-modal-close'
            >
              ×
            </button>
            {editablePrompts.length > 0 && (
              <div className='copilot-prompt-saved-list'>
                <h4 className='copilot-prompt-saved-list-title'>Your Saved Prompts</h4>
                {adminView && <h3>Default Prompts</h3>}
                <div className='copilot-prompt-saved-list-items'>
                  {adminView && (
                    <div className='copilot-prompt-saved-item'>
                      <div className='copilot-prompt-saved-item-meta'>
                        {
                          prompts.default.map(prompt => (
                            <div key={prompt?.id} className='copilot-prompt-saved-item'>
                              <div className='copilot-prompt-saved-item-meta'>
                                <div className='copilot-prompt-saved-item-name'>{prompt?.name}</div>
                                <div className='copilot-prompt-saved-item-preview'>{prompt?.prompt?.substring(0, 50)}...</div>
                              </div>
                              <div className='copilot-prompt-saved-item-actions'>
                                <button onClick={() => openEditModal(prompt)} className='copilot-prompt-edit-btn'>
                                  <FontAwesomeIcon icon={faPencil} />
                                </button>
                                <button onClick={() => deletePrompt(prompt?.id)} className='copilot-prompt-delete-item-btn'>
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </button>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                  {adminView && <h3>Not Default Prompts</h3>}
                  {prompts.custom.map(prompt => (
                    <div key={prompt?.id} className='copilot-prompt-saved-item'>
                      <div className='copilot-prompt-saved-item-meta'>
                        <div className='copilot-prompt-saved-item-name'>{prompt?.name}</div>
                        <div className='copilot-prompt-saved-item-preview'>{prompt?.prompt?.substring(0, 50)}...</div>
                      </div>
                      <div className='copilot-prompt-saved-item-actions'>
                        <button onClick={() => openEditModal(prompt)} className='copilot-prompt-edit-btn'>
                          <FontAwesomeIcon icon={faPencil} />
                        </button>
                        <button onClick={() => deletePrompt(prompt?.id)} className='copilot-prompt-delete-item-btn'>
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CopilotSlideOut;
